import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { ThemeConfig } from './useTheme';

export interface ChildTheme extends ThemeConfig {
  childId: string;
}

const defaultTheme: Omit<ChildTheme, 'childId'> = {
  mode: 'light',
  reducedMotion: false,
  fontSize: 'medium',
  colorIntensity: 'medium'
};

export function useChildTheme(childId: string | null) {
  const [childTheme, setChildTheme] = useState<ChildTheme | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (childId) {
      fetchChildTheme(childId);
    } else {
      setChildTheme(null);
      setLoading(false);
    }
  }, [childId]);

  const fetchChildTheme = async (id: string) => {
    setLoading(true);
    try {
      // Try to get the child's theme from Supabase
      const { data, error } = await supabase
        .from('child_preferences')
        .select('theme_settings')
        .eq('child_id', id)
        .limit(1);

      if (error) {
        console.error('Error fetching child theme:', error);
        const newTheme = { ...defaultTheme, childId: id };
        setChildTheme(newTheme);
        // Save the default theme to the database
        await saveChildTheme(newTheme);
      } else if (data && data.length > 0) {
        // Use the stored theme but don't apply to DOM (let ChildLayout handle it)
        const storedTheme = { 
          ...(data[0].theme_settings || defaultTheme), 
          childId: id 
        };
        setChildTheme(storedTheme);
      } else {
        // If no theme exists yet, create a default one
        const newTheme = { ...defaultTheme, childId: id };
        setChildTheme(newTheme);
        // Save the default theme to the database
        await saveChildTheme(newTheme);
      }
    } catch (error) {
      console.error('Error in fetchChildTheme:', error);
      const fallbackTheme = { ...defaultTheme, childId: id };
      setChildTheme(fallbackTheme);
    } finally {
      setLoading(false);
    }
  };

  const applyThemeToDOM = (themeConfig: ChildTheme) => {
    const root = document.documentElement;
    
    // Remove all theme classes
    root.classList.remove('theme-light', 'theme-dark', 'theme-high-contrast', 'theme-calm', 'theme-warm');
    root.classList.remove('reduced-motion', 'font-small', 'font-medium', 'font-large');
    root.classList.remove('color-low', 'color-medium', 'color-high');
    
    // Apply current theme
    root.classList.add(`theme-${themeConfig.mode}`);
    
    if (themeConfig.reducedMotion) {
      root.classList.add('reduced-motion');
    }
    
    root.classList.add(`font-${themeConfig.fontSize}`);
    root.classList.add(`color-${themeConfig.colorIntensity}`);
    
    // Set CSS custom properties for dynamic theming
    applyThemeVariables(themeConfig);
  };

  const applyThemeVariables = (themeConfig: ChildTheme) => {
    const root = document.documentElement;
    
    // Base color palettes for different themes
    const colorPalettes = {
      light: {
        '--bg-primary': '#ffffff',
        '--bg-secondary': '#f8fafc',
        '--bg-tertiary': '#f1f5f9',
        '--text-primary': '#1e293b',
        '--text-secondary': '#475569',
        '--text-muted': '#64748b',
        '--border-color': '#e2e8f0',
        '--accent-blue': '#3b82f6',
        '--accent-green': '#10b981',
        '--accent-purple': '#8b5cf6',
        '--accent-orange': '#f59e0b',
        '--accent-red': '#ef4444',
        '--accent-yellow': '#eab308',
        '--accent-cyan': '#06b6d4',
        '--accent-teal': '#14b8a6',
        '--shadow': '0 1px 3px 0 rgb(0 0 0 / 0.1)',
      },
      dark: {
        '--bg-primary': '#0f172a',
        '--bg-secondary': '#1e293b',
        '--bg-tertiary': '#334155',
        '--text-primary': '#f8fafc',
        '--text-secondary': '#cbd5e1',
        '--text-muted': '#94a3b8',
        '--border-color': '#475569',
        '--accent-blue': '#60a5fa',
        '--accent-green': '#34d399',
        '--accent-purple': '#a78bfa',
        '--accent-orange': '#fbbf24',
        '--accent-red': '#f87171',
        '--accent-yellow': '#fde047',
        '--accent-cyan': '#22d3ee',
        '--accent-teal': '#2dd4bf',
        '--shadow': '0 1px 3px 0 rgb(0 0 0 / 0.3)',
      },
      'high-contrast': {
        '--bg-primary': '#000000',
        '--bg-secondary': '#1a1a1a',
        '--bg-tertiary': '#333333',
        '--text-primary': '#ffffff',
        '--text-secondary': '#e0e0e0',
        '--text-muted': '#cccccc',
        '--border-color': '#666666',
        '--accent-blue': '#00bfff',
        '--accent-green': '#00ff00',
        '--accent-purple': '#ff00ff',
        '--accent-orange': '#ffa500',
        '--accent-red': '#ff0000',
        '--accent-yellow': '#ffff00',
        '--accent-cyan': '#00ffff',
        '--accent-teal': '#00ffbf',
        '--shadow': '0 2px 4px 0 rgb(255 255 255 / 0.1)',
      },
      calm: {
        '--bg-primary': '#f0f9ff',
        '--bg-secondary': '#e0f2fe',
        '--bg-tertiary': '#bae6fd',
        '--text-primary': '#0c4a6e',
        '--text-secondary': '#0369a1',
        '--text-muted': '#0284c7',
        '--border-color': '#7dd3fc',
        '--accent-blue': '#0ea5e9',
        '--accent-green': '#06b6d4',
        '--accent-purple': '#8b5cf6',
        '--accent-orange': '#f59e0b',
        '--accent-red': '#ef4444',
        '--accent-yellow': '#eab308',
        '--accent-cyan': '#0891b2',
        '--accent-teal': '#0d9488',
        '--shadow': '0 1px 3px 0 rgb(14 165 233 / 0.1)',
      },
      warm: {
        '--bg-primary': '#fefcf3',
        '--bg-secondary': '#fef3c7',
        '--bg-tertiary': '#fde68a',
        '--text-primary': '#78350f',
        '--text-secondary': '#92400e',
        '--text-muted': '#a16207',
        '--border-color': '#fbbf24',
        '--accent-blue': '#3b82f6',
        '--accent-green': '#10b981',
        '--accent-purple': '#8b5cf6',
        '--accent-orange': '#f59e0b',
        '--accent-red': '#ef4444',
        '--accent-yellow': '#eab308',
        '--accent-cyan': '#06b6d4',
        '--accent-teal': '#14b8a6',
        '--shadow': '0 1px 3px 0 rgb(245 158 11 / 0.1)',
      }
    };
    
    // Apply base colors
    const palette = colorPalettes[themeConfig.mode as keyof typeof colorPalettes];
    Object.entries(palette).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });
    
    // Apply font size scaling
    const fontSizes = {
      small: '0.875',
      medium: '1',
      large: '1.125'
    };
    root.style.setProperty('--font-scale', fontSizes[themeConfig.fontSize as keyof typeof fontSizes]);
    
    // Apply color intensity
    const intensities = {
      low: '0.7',
      medium: '1',
      high: '1.3'
    };
    root.style.setProperty('--color-intensity', intensities[themeConfig.colorIntensity as keyof typeof intensities]);
  };

  const saveChildTheme = async (theme: ChildTheme) => {
    if (!theme.childId) return;

    try {
      const { error } = await supabase
        .from('child_preferences')
        .upsert({
          child_id: theme.childId,
          theme_settings: {
            mode: theme.mode,
            reducedMotion: theme.reducedMotion,
            fontSize: theme.fontSize,
            colorIntensity: theme.colorIntensity
          },
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'child_id'
        });

      if (error) {
        console.error('Error saving child theme:', error);
      }
    } catch (error) {
      console.error('Error in saveChildTheme:', error);
    }
  };

  const updateChildTheme = async (updates: Partial<Omit<ChildTheme, 'childId'>>) => {
    if (!childTheme) return;
    
    const updatedTheme = { ...childTheme, ...updates };
    setChildTheme(updatedTheme);
    
    // Note: DOM theme application is handled by ChildLayout to prevent conflicts
    // Just save to database
    await saveChildTheme(updatedTheme);
  };

  return {
    childTheme,
    loading,
    updateChildTheme
  };
}